import { COMPANY_VALUATION_ANALYST_PRICE_TARGET } from '@/constants/feedbackSections';
import ChartWrapper from '@/pages/CompanyReport/components/Overview/components/ChartWrapper';
import { useValuationData } from '@/pages/CompanyReport/components/Valuation';
import { Column, LoadingSpinner, Text } from '@simplywallst/ui-core';
import {
  ValuationTranslation,
  useValuationTranslation,
} from '@/pages/CompanyReport/components/Valuation/hooks/useValuationTranslation';
import { AnalystPriceTargetTable } from './components/AnalystPriceTargetTable';
import { AnalystsPriceTargetsChart } from './components/AnalystPriceTargetsChart';
import { AnalystPriceTargetsLoading } from './components/AnalystPriceTargetsChart/styled';
import { minimumPriceTargets } from './constants';
import useAnalystPriceTargets from './hooks/useAnalystPriceTargets';
import { Link, Button } from '@simplywallst/ui-core/next';
import { ROUTE_NARRATIVE_COMMUNITY } from '@/constants/routes';
import { getPathSafe } from '@/router/utils';
import { useCompanyCanonicalUrl } from '@/hooks/useCompany';
import { getCompanyUrlSegments } from '@/pages/Narrative/utils/getNarrativeUrl';
import { NarrativeCardCompactSynthetic } from '@/pages/Narrative/components/NarrativeCardCompactSynthetic';
import { SWS_HOST } from '@/constants';

const HelpContent = () => (
  <div>
    <ValuationTranslation i18nKey="analystPriceTarget.help.description">
      <p>
        Analysts covering a company do extensive research on the underlying
        business and its prospects and they provide what's known as a price
        target. This price target is essentially an indication of where they
        think the stock price could be trading at in the future, usually 1
        year's time, based on their research and forecasts of the business'
        prospects.
      </p>
      <p>
        This Analyst Price Targets check tells us whether there might be an
        upside opportunity with the stock, and if we should have confidence in
        that consensus price target.
      </p>
      <p>
        The chart represents the historical share price (blue line) with what
        the consensus price target was at that time looking forward 1 year (the
        purple line). The shaded area also shows us the range of analysts
        estimates at that time, from highest to lowest, which shows us how much
        they agreed (or disagreed) with each other.
      </p>
      <p>
        There are two criteria needed for a company to pass the Analyst Price
        Targets check. Firstly, the current share price must be at least 20%
        below the consensus price target. This is to indicate if there might be
        any decent upside opportunity with the stock. Secondly, the dispersion
        from the consensus must be less than 15%, meaning analysts expectations
        are largely the same.
      </p>
      <p>
        For a more detailed breakdown of how the Analyst Price Targets chart
        works
        <Link
          target="_blank"
          href={`${SWS_HOST}/help/articles/hc/en-us/articles/4751563581071-Understanding-the-Valuation-section-in-the-company-report`}
          rel="noopener noreferrer"
        >
          check out our detailed explanation on the help centre.
        </Link>
      </p>
    </ValuationTranslation>
  </div>
);

export const AnalystPriceTargets = ({
  isDataTrustworthy,
}: {
  isDataTrustworthy?: boolean | null;
}) => {
  const valuationData = useValuationData();

  const {
    priceData: { priceData, priceMin, priceMax, dayZero },
    priceTarget: {
      priceTargetData, //Analyst Prediction Target {date,price}
      priceTargetKeys, //Analyst Prediction Dates only
      analystData,
      priceTargetMax,
      priceTargetMin,
      priceTargetRangeHigh,
      priceTargetRangeLow,
    },
    priceDataStatus,
    priceTargetStatus,
  } = useAnalystPriceTargets();

  const canonicalUrl = useCompanyCanonicalUrl();
  const t = useValuationTranslation();
  const urlSegments = getCompanyUrlSegments(canonicalUrl);
  const narrativeUrl = getPathSafe({
    type: ROUTE_NARRATIVE_COMMUNITY,
    payload: {
      country: urlSegments.country,
      industry: urlSegments.industry,
      companyname: urlSegments.companyName,
      exchange_ticker: urlSegments.exchangeTicker,
    },
  });

  return (
    <ChartWrapper
      id="analyst-price-targets"
      chart={
        priceDataStatus === 'success' && priceTargetStatus === 'success' ? (
          <AnalystsPriceTargetsChart
            currencyISO={valuationData.currencyISO}
            useNewCalcFlag={valuationData.shouldShowAptCalc}
            priceTargetData={priceTargetData}
            priceTargetKeys={priceTargetKeys}
            analystData={analystData}
            priceTargetMax={priceTargetMax}
            priceTargetMin={priceTargetMin}
            priceTargetRangeHigh={priceTargetRangeHigh}
            priceTargetRangeLow={priceTargetRangeLow}
            priceData={priceData}
            priceMax={priceMax}
            priceMin={priceMin}
            dayZero={dayZero}
            isDataTrustworthy={isDataTrustworthy}
          />
        ) : (
          <AnalystPriceTargetsLoading>
            <Column
              col={12}
              displayFlex
              justifyContent="center"
              alignItem="center"
              column
            >
              <LoadingSpinner light />
              <Text typography="tiny">Fetching data</Text>
            </Column>
          </AnalystPriceTargetsLoading>
        )
      }
      table={
        typeof isDataTrustworthy === 'boolean' &&
        priceTargetStatus === 'success' &&
        priceTargetData.length >= minimumPriceTargets ? (
          <AnalystPriceTargetTable
            tickerSymbol={valuationData.tickerSymbol}
            currencyISO={valuationData.currencyISO}
            priceData={priceData}
            priceTargetData={priceTargetData}
            analystData={analystData}
          />
        ) : undefined
      }
      feedbackLabel={COMPANY_VALUATION_ANALYST_PRICE_TARGET}
      moreActions={
        narrativeUrl ? (
          <Button variant="outline" size="medium" href={narrativeUrl}>
            Explore Narratives
          </Button>
        ) : undefined
      }
      help={<HelpContent />}
      helpTitle={t('analystPriceTarget.help.title')}
      preRenderHeight={490}
      preRenderHeightMdDown={540}
      preRenderHeightSmDown={440}
    >
      <NarrativeCardCompactSynthetic
        canonicalUrl={canonicalUrl}
        type="ANALYSTS"
      />
    </ChartWrapper>
  );
};
